import { formatDateTime } from '../utils/dateUtils';

export type TaskArea = 'frontend' | 'backend' | 'design' | 'Q&A';
export type TaskPriority = 'baixa' | 'media' | 'alta' | 'urgente';
export type TaskStatus = 'pendente' | 'em_progresso' | 'pausado' | 'encerrada';
export interface Task {
  _id: string;
  name: string;
  description?: string;
  area: TaskArea;
  horasEstimadas: number;
  prioridade: TaskPriority;
  status: TaskStatus;
  projectId: string;
  userId: string;
  serviceOrderId?: string;
  jiraIssueKey?: string;
  lastJiraSync?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTaskRequest {
  name: string;
  description?: string;
  area: TaskArea;
  horasEstimadas: number;
  prioridade: TaskPriority;
  status?: TaskStatus;
}

export interface UpdateTaskRequest {
  name?: string;
  description?: string;
  area?: TaskArea;
  horasEstimadas?: number;
  prioridade?: TaskPriority;
  status?: TaskStatus;
}

export interface TaskStatistics {
  total: number;
  pendente: number;
  em_progresso: number;
  pausado: number;
  encerrada: number;
  totalHoras: number;
  horasCompletas: number;
}

export interface TaskListResponse {
  success: boolean;
  data: {
    tasks: Task[];
    statistics: TaskStatistics;
    count: number;
  };
}

export interface TaskResponse {
  success: boolean;
  message?: string;
  data: {
    task: Task;
  };
}

export interface TaskError {
  success: false;
  message: string;
  errors?: any[];
}

// Helper types for UI labels
export const TaskAreaLabels: Record<TaskArea, string> = {
  'frontend': 'Frontend',
  'backend': 'Backend',
  'design': 'Design',
  'Q&A': 'Q&A'
};

export const TaskPriorityLabels: Record<TaskPriority, string> = {
  'baixa': 'Baixa',
  'media': 'Média',
  'alta': 'Alta',
  'urgente': 'Urgente'
};

export const TaskStatusLabels: Record<TaskStatus, string> = {
  'pendente': 'Pendente',
  'em_progresso': 'Em Progresso',
  'pausado': 'Pausado',
  'encerrada': 'Encerrada'
};

// Priority colors for UI (light text for dark theme)
export const TaskPriorityColors: Record<TaskPriority, string> = {
  'baixa': 'bg-green-500/20 text-green-300 border-green-500/30',
  'media': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  'alta': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  'urgente': 'bg-red-500/20 text-red-300 border-red-500/30'
};

// Status colors for UI (light text for dark theme)
export const TaskStatusColors: Record<TaskStatus, string> = {
  'pendente': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'em_progresso': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  'pausado': 'bg-gray-500/20 text-gray-300 border-gray-500/30',
  'encerrada': 'bg-green-500/20 text-green-300 border-green-500/30'
};

// Area colors for UI (light text for dark theme)
export const TaskAreaColors: Record<TaskArea, string> = {
  'frontend': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'backend': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  'design': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
  'Q&A': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
};

// JIRA Integration Types and Utilities
export interface JiraConfig {
  projectKey: string;
  jiraUrl: string;
  username: string;
}

export interface TaskWithJira extends Task {
  jiraIssueKey: string;
  lastJiraSync: string;
}

// JIRA URL builder helper
export const buildJiraIssueUrl = (jiraUrl: string, issueKey: string): string => {
  const cleanUrl = jiraUrl.endsWith('/') ? jiraUrl.slice(0, -1) : jiraUrl;
  return `${cleanUrl}/browse/${issueKey}`;
};

// Check if task is synced with JIRA
export const isTaskSyncedWithJira = (task: Task): task is TaskWithJira => {
  return !!(task.jiraIssueKey && task.lastJiraSync);
};

// Format last sync date for display

export const formatJiraSyncDate = (syncDate: string): string => {
  try {
    return formatDateTime(syncDate);
  } catch {
    return 'Data inválida';
  }
};
